import { MultiSelectFieldHandle, MultiSelectFieldProps } from "./types";
import { forwardRef, useCallback, useId, useRef } from "react";
import { useLayoutPropsUtil } from "../../../internal/hooks/useLayoutPropsUtil";
import { warnDeprecatedErrorUsage } from "../../../components/FieldMessage";
import { warnOnce } from "../../../internal/functions";
import { CheckState } from "../../../types";
import { MultiSelectFieldComboboxMode } from "./internal/MultiSelectFieldComboboxMode";
import { MultiSelectFieldSelectMode } from "./internal/MultiSelectFieldSelectMode";
import {
  MultiSelectFieldBaseInternalProps,
  MultiSelectFieldComboboxInternalProps,
} from "./internal/types";
import { useSelectOrchestration } from "../../../internal/hooks/useSelectOrchestration";

/**
 * MultiSelectField component for selecting multiple options from a searchable dropdown.
 *
 * Features:
 * - Multiple selection with chip display
 * - Search and filtering capabilities (disable with `disableSearch`)
 * - Lazy loading with pagination (page, offset, or group modes)
 * - Optional "Select All" functionality
 * - Optional "Select Filtered" functionality (selects options matching current search)
 * - Sticky positioning for Select All / Select Filtered items
 * - Supports both popover and dialog display modes
 * - Adaptive display based on device (mobile uses dialog)
 * - Cache support for loadOptions results
 * - Single row mode to restrict height (singleRow prop)
 * - Chip count limiting (maxChips prop)
 * - Full accessibility support with ARIA attributes
 * - Controlled selection state
 */
export const MultiSelectField = forwardRef<
  MultiSelectFieldHandle,
  MultiSelectFieldProps
>((propsWithLayout, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(propsWithLayout);

  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const {
    id: idProp,
    value: selectedOptions,
    initialLoad: initialLoadProp,
    displayMenuAs: displayMenuAsProp,
    lazy,
    loadOptions: loadOptionsProp,
    label,
    hideLabel,
    placeholder,
    debounceMs = 200,
    onSelectedOptionsChange,
    cache,
    size,
    error,
    hint,
    description,
    required,
    labelAiMark,
    disabled,
    readOnly,
    prefix,
    suffix,
    selectAll: selectAllProp,
    selectFiltered: selectFilteredProp,
    singleRow,
    maxChips,
    warning,
    pinned,
    onSearchChange,
    className,
    style,
    virtualize,
    disableSearch,
    getChipProps,
  } = componentProps;

  const groupToString =
    "groupToString" in componentProps
      ? componentProps.groupToString
      : undefined;
  const groupSorter =
    "groupSorter" in componentProps ? componentProps.groupSorter : undefined;

  const autoId = useId();
  const id = idProp ?? autoId;
  const helperUid = useId();

  warnDeprecatedErrorUsage("MultiSelectField", error);

  if (disableSearch && selectFilteredProp) {
    warnOnce(
      "MultiSelectField: `selectFiltered` has no effect when `disableSearch` is true because there is no search input to produce filtered results.",
    );
  }

  const errorMessages =
    typeof error === "boolean" || error === undefined ? undefined : error;
  const hasHelperText = !!(hint || errorMessages || warning || description);
  const isDisabledOrReadOnly = (disabled || readOnly) ?? false;

  const {
    displayAs,
    initialLoad,
    initialLoadPerformed,
    setInitialLoadPerformed,
    options,
    loading,
    loadingMore,
    hasMore,
    loadOptions,
    loadMore,
  } = useSelectOrchestration({
    lazy,
    loadOptions: loadOptionsProp,
    cache,
    lazyOptions:
      "lazyOptions" in componentProps ? componentProps.lazyOptions : undefined,
    initialLoad: initialLoadProp,
    displayMenuAs: displayMenuAsProp,
    ref,
  });

  const handleRemoveOption = useCallback(
    (option: { id: string | number }) => {
      onSelectedOptionsChange(
        selectedOptions.filter((opt) => opt.id !== option.id),
      );
    },
    [selectedOptions, onSelectedOptionsChange],
  );

  const handleClear = useCallback(() => {
    onSelectedOptionsChange([]);
  }, [onSelectedOptionsChange]);

  const disableClearButton =
    selectedOptions.length === 0 || isDisabledOrReadOnly;

  const selectAll = selectAllProp
    ? {
        label: selectAllProp.label ?? "Select All",
        onClick: selectAllProp.onClick,
        checkState: (selectAllProp.checkState === true
          ? "checked"
          : selectAllProp.checkState === false
            ? "unchecked"
            : (selectAllProp.checkState ?? "indeterminate")) as CheckState,
      }
    : undefined;

  const selectFiltered = selectFilteredProp
    ? (searchValue: string) => {
        const config = selectFilteredProp(searchValue);
        return {
          label: config.label,
          onClick: config.onClick,
          checkState: (config.checkState === true
            ? "checked"
            : config.checkState === false
              ? "unchecked"
              : (config.checkState ?? "indeterminate")) as CheckState,
        };
      }
    : undefined;

  const baseProps: MultiSelectFieldBaseInternalProps = {
    id,
    helperUid,
    selectedOptions,
    onSelectedOptionsChange,
    displayAs,
    isDisabledOrReadOnly,
    disabled,
    readOnly,
    required,
    placeholder,
    size,
    error,
    hasHelperText,
    disableClearButton,
    prefix,
    suffix,
    label,
    hideLabel,
    hint,
    labelAiMark,
    errorMessages,
    warning,
    description,
    className,
    style,
    layoutStyles,
    pinned,
    groupToString,
    groupSorter,
    virtualize,
    options,
    loading,
    loadingMore,
    hasMore,
    loadOptions,
    loadMore,
    initialLoad,
    initialLoadPerformed,
    setInitialLoadPerformed,
    inputWrapperRef,
    selectAll,
    singleRow,
    maxChips,
    getChipProps,
    handleRemoveOption,
    handleClear,
  };

  if (disableSearch) {
    return <MultiSelectFieldSelectMode {...baseProps} />;
  }

  const comboboxProps: MultiSelectFieldComboboxInternalProps = {
    ...baseProps,
    debounceMs,
    selectFiltered,
    onSearchChange,
  };

  return <MultiSelectFieldComboboxMode {...comboboxProps} />;
});

MultiSelectField.displayName = "MultiSelectField";
